import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    getQuote, createQuote, updateQuote, addQuoteLine, updateQuoteLine,
    deleteQuoteLine, deleteQuote, resolveVariants, getCustomProducts, reviseQuote, updateQuoteStatus, getQuotePdfUrl, duplicateQuote, updateInternalNotes
} from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../lib/auth';
import { CustomerPicker } from '../components/CustomerPicker';
import {
    ArrowLeft, Plus, Trash, Eye, FloppyDisk, Package,
    X, ArrowsClockwise, MagnifyingGlass, WarningCircle, CheckCircle,
    CaretDown, Funnel, Image as ImageIcon, Tag, Truck, FilePdf, Copy, ToggleLeft, ToggleRight
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const MIN_ORDER_VALUE = 5000;
const SHIPPING_SURCHARGE = 400;
const THRESHOLD_CURRENCY = 'EUR';

function dimWithUnit(d) { if (!d) return ''; return d.toString().toLowerCase().includes('mm') ? d : `${d} mm`; }

const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'internal_review', label: 'Internal Review' },
    { value: 'sent', label: 'Sent to Customer' },
    { value: 'revised', label: 'Revised' },
    { value: 'won', label: 'Won' },
    { value: 'lost', label: 'Lost' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'archived', label: 'Archived' },
];

function ProductImage({ src, alt, size = 'md' }) {
    const dims = size === 'xs' ? 'w-8 h-8' : size === 'sm' ? 'w-12 h-12' : size === 'md' ? 'w-16 h-16' : 'w-24 h-24';
    const iconSize = size === 'xs' ? 12 : size === 'sm' ? 16 : size === 'md' ? 22 : 28;
    if (!src) {
        return (
            <div className={`${dims} rounded-md bg-muted/60 flex items-center justify-center flex-shrink-0 border border-border/40`}>
                <ImageIcon size={iconSize} className="text-muted-foreground/50" />
            </div>
        );
    }
    return (
        <img
            src={src}
            alt={alt || 'Product'}
            className={`${dims} rounded-md object-cover flex-shrink-0 bg-muted border border-border/40`}
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
        />
    );
}

// ============ CATALOG CONFIGURATOR (Search + Filters) ============
function CatalogConfigurator({ currency, onAdd, onClose }) {
    const [searchText, setSearchText] = useState('');
    const [selected, setSelected] = useState({});
    const [available, setAvailable] = useState({});
    const [matches, setMatches] = useState([]);
    const [totalMatches, setTotalMatches] = useState(0);
    const [loading, setLoading] = useState(false);
    const [chosenProduct, setChosenProduct] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [quotedPrice, setQuotedPrice] = useState('');
    const [quantity, setQuantity] = useState('');
    const [overrideReason, setOverrideReason] = useState('');
    const searchTimer = useRef(null);

    const resolve = useCallback(async (sel, search = '', page = 0) => {
        setLoading(true);
        try {
            const res = await resolveVariants(sel, search, page);
            const d = res.data;
            setAvailable(d.available || {});
            setMatches(d.matches || []);
            setTotalMatches(d.total_matches || 0);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { resolve({}, '', 0); }, [resolve]);

    const handleSearchChange = (val) => {
        setSearchText(val);
        setChosenProduct(null);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            resolve(selected, val, 0);
        }, 300);
    };

    const handleSelect = (key, value) => {
        const newSel = { ...selected, [key]: value };
        setSelected(newSel);
        setChosenProduct(null);
        resolve(newSel, searchText, 0);
    };

    const handleClear = (key) => {
        const newSel = { ...selected };
        delete newSel[key];
        setSelected(newSel);
        setChosenProduct(null);
        resolve(newSel, searchText, 0);
    };

    const handleReset = () => {
        setSelected({});
        setSearchText('');
        setChosenProduct(null);
        setShowFilters(false);
        resolve({}, '', 0);
    };

    const handlePickProduct = (p) => {
        setChosenProduct(p);
        setQuotedPrice(p.eur_price ? String(p.eur_price) : '');
        setQuantity('');
        setOverrideReason('');
    };

    const handleAddToQuote = () => {
        if (!chosenProduct) return;
        const p = chosenProduct;
        const imgUrl = p.primary_image?.url || p.primary_image?.file_url || '';
        const imgId = p.primary_image?.id || '';
        const finalPrice = parseFloat(quotedPrice) || p.eur_price || 0;
        const qty = parseInt(quantity) || 0;

        onAdd({
            line_type: 'catalog',
            product_key: p.product_key,
            display_sku: p.display_sku,
            display_code: [p.display_sku, p.dimensions, p.thickness_text || (p.thickness_mm ? p.thickness_mm + 'mm' : ''), p.decor_no ? 'D' + p.decor_no : '', p.finish].filter(Boolean).join(' | '),
            product_name: p.product_name,
            dimensions: p.dimensions,
            thickness: p.thickness_text || (p.thickness_mm ? `${p.thickness_mm} mm` : ''),
            decor_no: p.decor_no,
            decor_name: p.decor_name,
            finish: p.finish,
            default_price: p.eur_price || 0,
            quoted_price: finalPrice,
            override_reason: finalPrice !== (p.eur_price || 0) ? overrideReason : '',
            currency: currency || 'EUR',
            moq: p.computed_moq,
            quantity: qty,
            unit_of_sale: p.unit_of_sale || 'piece',
            packaging_summary: p.packaging_summary || '',
            image_url: imgUrl,
            image_id: imgId,
        });
    };

    const activeFilterCount = Object.keys(selected).length;
    const filterKeys = ['category', 'variant_group', 'size_mm', 'thickness_mm', 'decor_no', 'finish'];
    const filterLabels = { category: 'Category', variant_group: 'Family', size_mm: 'Size', thickness_mm: 'Thickness', decor_no: 'Decor', finish: 'Finish' };

    return (
        <div className="space-y-3" data-testid="catalog-configurator">
            {/* Search bar */}
            <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                    placeholder="Search by SKU, product name, family, decor, size, thickness..."
                    value={searchText}
                    onChange={e => handleSearchChange(e.target.value)}
                    className="pl-9 h-10"
                    data-testid="catalog-search-input"
                    autoFocus
                />
                {searchText && (
                    <button className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => handleSearchChange('')}>
                        <X size={14} />
                    </button>
                )}
            </div>

            {/* Filter toggle + active count */}
            <div className="flex items-center justify-between">
                <Button
                    variant={showFilters ? 'secondary' : 'outline'}
                    size="sm"
                    className="h-7 text-xs gap-1.5"
                    onClick={() => setShowFilters(!showFilters)}
                    data-testid="toggle-filters-btn"
                >
                    <Funnel size={13} />
                    Filters
                    {activeFilterCount > 0 && <Badge className="h-4 w-4 p-0 text-[10px] flex items-center justify-center rounded-full">{activeFilterCount}</Badge>}
                </Button>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                        {loading ? 'Searching...' : `${totalMatches} product${totalMatches !== 1 ? 's' : ''}`}
                    </span>
                    {(activeFilterCount > 0 || searchText) && (
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleReset} data-testid="reset-filters-btn">
                            <ArrowsClockwise size={12} className="mr-1" /> Clear all
                        </Button>
                    )}
                </div>
            </div>

            {/* Dynamic Filters */}
            {showFilters && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 p-3 bg-muted/30 rounded-lg border border-border" data-testid="filter-panel">
                    {filterKeys.map(key => {
                        const opts = available[key] || [];
                        return (
                            <div key={key}>
                                <Label className="text-[11px] text-muted-foreground mb-1 block">{filterLabels[key]}</Label>
                                <div className="flex gap-1">
                                    <Select
                                        value={selected[key] != null ? String(selected[key]) : "any"}
                                        onValueChange={(v) => v === "any" ? handleClear(key) : handleSelect(key, key === 'thickness_mm' ? parseFloat(v) : v)}
                                    >
                                        <SelectTrigger className="text-xs h-8" data-testid={`config-${key}`}>
                                            <SelectValue placeholder={`Any`} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="any">Any</SelectItem>
                                            {opts.map(o => (
                                                <SelectItem key={String(o)} value={String(o)}>
                                                    {key === 'thickness_mm' ? `${o} mm` : String(o)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {selected[key] != null && (
                                        <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => handleClear(key)}>
                                            <X size={12} />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Product matches list */}
            {!chosenProduct && (
                <div className="max-h-72 overflow-y-auto border border-border rounded-lg divide-y divide-border" data-testid="product-matches-list">
                    {loading ? (
                        <div className="p-6 text-center text-sm text-muted-foreground">Searching products...</div>
                    ) : matches.length === 0 ? (
                        <div className="p-6 text-center text-sm text-muted-foreground">
                            {searchText || activeFilterCount > 0 ? 'No products match your search. Try different terms or clear filters.' : 'Start typing to search products.'}
                        </div>
                    ) : matches.map(p => (
                        <button
                            key={p.product_key}
                            className="w-full text-left p-3 hover:bg-muted/50 transition-colors flex items-center gap-3"
                            onClick={() => handlePickProduct(p)}
                            data-testid={`match-${p.product_key}`}
                        >
                            <ProductImage src={p.primary_image?.file_url} alt={p.product_name} size="md" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-xs font-semibold">{p.display_sku}</span>
                                    {p.category && <Badge variant="outline" className="text-[10px] h-4">{p.category}</Badge>}
                                </div>
                                <div className="text-sm font-medium truncate mt-0.5">{p.product_name}</div>
                                <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5">
                                    {p.dimensions && <span>{dimWithUnit(p.dimensions)}</span>}
                                    {p.thickness_mm && <span>| {p.thickness_mm} mm</span>}
                                    {p.decor_no && <span>| D{p.decor_no}</span>}
                                    {p.finish && <span>| {p.finish}</span>}
                                </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                                <div className="font-mono text-sm font-semibold">{p.eur_price ? formatCurrency(p.eur_price, 'EUR') : '—'}</div>
                                <div className="text-[11px] text-muted-foreground">Rec. MOQ: {p.computed_moq?.toLocaleString() || '—'}</div>
                            </div>
                        </button>
                    ))}
                    {!loading && totalMatches > matches.length && (
                        <div className="p-2 text-center text-xs text-muted-foreground">
                            Showing {matches.length} of {totalMatches}. Refine search for better results.
                        </div>
                    )}
                </div>
            )}

            {/* Selected product detail + add form */}
            {chosenProduct && (
                <div className="border-2 border-primary/30 bg-primary/5 rounded-lg p-4 space-y-4" data-testid="selected-product-preview">
                    <div className="flex items-start gap-4">
                        <ProductImage src={chosenProduct.primary_image?.file_url} alt={chosenProduct.product_name} size="lg" />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="font-mono text-sm font-semibold">{chosenProduct.display_sku}</div>
                                    <div className="text-base font-semibold">{chosenProduct.product_name}</div>
                                </div>
                                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setChosenProduct(null)}>
                                    <X size={12} className="mr-1" /> Change
                                </Button>
                            </div>
                            <div className="grid grid-cols-3 gap-x-4 gap-y-1 mt-2 text-sm">
                                <div><span className="text-muted-foreground text-[11px] block">Dimensions</span><span className="font-mono text-xs">{dimWithUnit(chosenProduct.dimensions) || '—'}</span></div>
                                <div><span className="text-muted-foreground text-[11px] block">Thickness</span><span className="font-mono text-xs">{chosenProduct.thickness_text || (chosenProduct.thickness_mm ? `${chosenProduct.thickness_mm} mm` : '—')}</span></div>
                                <div><span className="text-muted-foreground text-[11px] block">Decor</span><span className="font-mono text-xs">{chosenProduct.decor_no || '—'}{chosenProduct.decor_name ? ` — ${chosenProduct.decor_name}` : ''}</span></div>
                                <div><span className="text-muted-foreground text-[11px] block">Finish</span><span className="text-xs">{chosenProduct.finish || '—'}</span></div>
                                <div><span className="text-muted-foreground text-[11px] block">Packaging</span><span className="text-xs">{chosenProduct.packaging_summary || '—'}</span></div>
                                <div><span className="text-muted-foreground text-[11px] block">Family</span><span className="text-xs">{chosenProduct.variant_group || '—'}</span></div>
                            </div>
                        </div>
                    </div>

                    {/* Pricing + Quantity form */}
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label className="text-[11px] text-muted-foreground mb-1 block">
                                Price ({currency}) {chosenProduct.eur_price ? <span className="text-muted-foreground">Default: {formatCurrency(chosenProduct.eur_price, 'EUR')}</span> : ''}
                            </Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={quotedPrice}
                                onChange={e => setQuotedPrice(e.target.value)}
                                className="font-mono h-9"
                                placeholder="Price"
                                data-testid="config-price"
                            />
                        </div>
                        <div>
                            <Label className="text-[11px] text-muted-foreground mb-1 block">
                                Quantity {chosenProduct.computed_moq ? <span className="text-muted-foreground">MOQ: {chosenProduct.computed_moq.toLocaleString()}</span> : ''}
                            </Label>
                            <Input
                                type="number"
                                value={quantity}
                                onChange={e => setQuantity(e.target.value)}
                                className="font-mono h-9"
                                placeholder="Enter qty"
                                data-testid="config-quantity"
                            />
                            {quantity && chosenProduct.computed_moq && parseInt(quantity) < chosenProduct.computed_moq && parseInt(quantity) > 0 && (
                                <div className="flex items-center gap-1 mt-1 text-amber-600 text-[11px]" data-testid="moq-warning">
                                    <WarningCircle size={12} /> Below recommended MOQ ({chosenProduct.computed_moq.toLocaleString()})
                                </div>
                            )}
                        </div>
                        <div>
                            <Label className="text-[11px] text-muted-foreground mb-1 block">Override Reason</Label>
                            <Input
                                value={overrideReason}
                                onChange={e => setOverrideReason(e.target.value)}
                                className="h-9 text-xs"
                                placeholder="Optional note"
                                data-testid="config-override-reason"
                            />
                        </div>
                    </div>

                    {/* Price override indicator */}
                    {quotedPrice && chosenProduct.eur_price && parseFloat(quotedPrice) !== chosenProduct.eur_price && (
                        <div className="flex items-center gap-2 text-xs px-2 py-1.5 rounded bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800">
                            <Tag size={13} />
                            Price override: {formatCurrency(chosenProduct.eur_price, 'EUR')} → {formatCurrency(parseFloat(quotedPrice), currency)}
                            {overrideReason && <span className="ml-1 text-muted-foreground">({overrideReason})</span>}
                        </div>
                    )}

                    <Button onClick={handleAddToQuote} className="w-full relative z-10" data-testid="add-catalog-to-quote" disabled={!quantity || parseInt(quantity) <= 0}>
                        <Plus size={16} className="mr-2" /> Add to Quote
                    </Button>
                </div>
            )}
        </div>
    );
}

// ============ CUSTOM PRODUCT SELECTOR ============
function CustomProductSelector({ currency, onAdd }) {
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        getCustomProducts({ search, limit: 20 }).then(r => setProducts(r.data.products)).catch(() => {}).finally(() => setLoading(false));
    }, [search]);

    const handleAdd = (p) => {
        onAdd({
            line_type: 'custom',
            custom_product_id: p.id,
            display_code: p.custom_code,
            display_sku: p.custom_code,
            product_name: p.product_name,
            dimensions: p.dimensions,
            thickness: p.thickness,
            decor_no: p.decor_no,
            decor_name: p.decor_name,
            finish: p.finish,
            default_price: p.quoted_price || 0,
            quoted_price: p.quoted_price || 0,
            currency: currency || 'EUR',
            moq: 0,
            quantity: 0,
        });
    };

    return (
        <div className="space-y-3">
            <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Search custom products..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="custom-product-search" />
            </div>
            <div className="max-h-64 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                {loading ? <div className="p-4 text-center text-muted-foreground text-sm">Loading...</div>
                : products.length === 0 ? <div className="p-4 text-center text-muted-foreground text-sm">No custom products found</div>
                : products.map(p => (
                    <button key={p.id} className="w-full text-left p-3 hover:bg-muted/50 transition-colors flex items-center justify-between"
                        onClick={() => handleAdd(p)} data-testid={`custom-pick-${p.id}`}>
                        <div>
                            <span className="font-mono text-sm font-medium">{p.custom_code}</span>
                            <span className="text-sm ml-2">{p.product_name}</span>
                            <div className="text-xs text-muted-foreground">{p.customer_name} {p.dimensions && `| ${p.dimensions}`}</div>
                        </div>
                        <div className="text-right">
                            <div className="font-mono text-sm">{p.quoted_price ? formatCurrency(p.quoted_price, 'EUR') : '—'}</div>
                            <Badge variant="outline" className="text-[10px]">{p.status}</Badge>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}

// ============ MANUAL LINE FORM ============
function ManualLineForm({ currency, onAdd }) {
    const [form, setForm] = useState({ description: '', quoted_price: '', quantity: 1, notes: '' });
    const onChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

    const handleAdd = () => {
        if (!form.description.trim()) { toast.error('Description required'); return; }
        onAdd({
            line_type: 'manual',
            display_code: form.description,
            product_name: form.description,
            description: form.description,
            default_price: parseFloat(form.quoted_price) || 0,
            quoted_price: parseFloat(form.quoted_price) || 0,
            currency: currency || 'EUR',
            quantity: parseInt(form.quantity) || 1,
            notes: form.notes,
        });
        setForm({ description: '', quoted_price: '', quantity: 1, notes: '' });
    };

    const presets = ['Development Charges', 'Tooling Charges', 'Special Packaging', 'Freight', 'Sample Charges', 'Miscellaneous'];

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-1.5">
                {presets.map(p => (
                    <Button key={p} variant="outline" size="sm" className="text-xs h-7" onClick={() => setForm(f => ({ ...f, description: p }))} data-testid={`preset-${p.toLowerCase().replace(/\s+/g, '-')}`}>
                        {p}
                    </Button>
                ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                    <Label className="text-xs text-muted-foreground mb-1 block">Description</Label>
                    <Input name="description" value={form.description} onChange={onChange} placeholder="e.g. Tooling Charges" data-testid="manual-description" />
                </div>
                <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Amount ({currency})</Label>
                    <Input name="quoted_price" type="number" value={form.quoted_price} onChange={onChange} className="font-mono" data-testid="manual-price" />
                </div>
                <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Quantity</Label>
                    <Input name="quantity" type="number" value={form.quantity} onChange={onChange} className="font-mono" data-testid="manual-qty" />
                </div>
                <div className="sm:col-span-2">
                    <Label className="text-xs text-muted-foreground mb-1 block">Notes</Label>
                    <Input name="notes" value={form.notes} onChange={onChange} placeholder="Optional" />
                </div>
            </div>
            <Button onClick={handleAdd} className="w-full" data-testid="add-manual-line">
                <Plus size={16} className="mr-2" /> Add Line
            </Button>
        </div>
    );
}

// ============ COMMERCIAL SUMMARY ============
function CommercialSummary({ lines, currency, surchargeApplied, onToggleSurcharge }) {
    const subtotal = lines.reduce((sum, l) => sum + (l.line_total || 0), 0);
    const isEUR = currency === 'EUR';
    const belowThreshold = isEUR && subtotal > 0 && subtotal < MIN_ORDER_VALUE;
    const surchargeAmount = surchargeApplied ? SHIPPING_SURCHARGE : 0;
    const finalTotal = subtotal + surchargeAmount;

    return (
        <div className="border-t border-border" data-testid="commercial-summary">
            {/* MOQ-below warnings per line */}
            {lines.some(l => l.line_type === 'catalog' && l.moq && l.quantity > 0 && l.quantity < l.moq) && (
                <div className="px-5 py-2 bg-amber-50/50 border-b border-amber-100 dark:bg-amber-950/20 dark:border-amber-900/30">
                    <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400">
                        <WarningCircle size={14} className="mt-0.5 flex-shrink-0" />
                        <div>
                            <span className="font-medium">Below recommended MOQ:</span>{' '}
                            {lines.filter(l => l.line_type === 'catalog' && l.moq && l.quantity > 0 && l.quantity < l.moq).map(l => l.display_sku || l.display_code).join(', ')}
                        </div>
                    </div>
                </div>
            )}

            <div className="px-5 py-4 space-y-2.5">
                {/* Subtotal */}
                <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal ({currency})</span>
                    <span className="font-mono font-medium" data-testid="quote-subtotal">{formatCurrency(subtotal, currency)}</span>
                </div>

                {/* Threshold indicator (EUR only) */}
                {isEUR && subtotal > 0 && (
                    <>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Min order threshold</span>
                            <span className="font-mono text-muted-foreground">{formatCurrency(MIN_ORDER_VALUE, 'EUR')}</span>
                        </div>

                        {!belowThreshold ? (
                            <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400" data-testid="threshold-met">
                                <CheckCircle size={14} weight="fill" />
                                <span className="font-medium">Meets minimum order value</span>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400" data-testid="threshold-not-met">
                                    <WarningCircle size={14} weight="fill" />
                                    <span className="font-medium">Below minimum order value</span>
                                </div>
                                {/* Surcharge toggle */}
                                <button
                                    className={`flex items-center justify-between w-full text-sm px-3 py-2 rounded-md border transition-colors ${surchargeApplied ? 'border-amber-300 bg-amber-50/80 dark:border-amber-700 dark:bg-amber-950/30' : 'border-border bg-muted/30'}`}
                                    onClick={onToggleSurcharge}
                                    data-testid="surcharge-toggle"
                                >
                                    <span className="flex items-center gap-2">
                                        <Truck size={14} className={surchargeApplied ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'} />
                                        <span className={surchargeApplied ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}>
                                            Apply EUR 400 shipping surcharge
                                        </span>
                                    </span>
                                    {surchargeApplied
                                        ? <ToggleRight size={24} weight="fill" className="text-amber-600 dark:text-amber-400" />
                                        : <ToggleLeft size={24} className="text-muted-foreground" />
                                    }
                                </button>
                                {surchargeApplied && (
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                                            <Truck size={14} /> Shipping surcharge
                                        </span>
                                        <span className="font-mono font-medium text-amber-600 dark:text-amber-400" data-testid="shipping-surcharge">
                                            + {formatCurrency(SHIPPING_SURCHARGE, 'EUR')}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                {/* Non-EUR note */}
                {!isEUR && subtotal > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <WarningCircle size={13} />
                        Min order EUR 5,000 equivalent. Surcharge of EUR 400 may apply if below threshold.
                    </div>
                )}

                {/* Divider + Final total */}
                <div className="border-t border-border pt-2 flex items-center justify-between">
                    <span className="font-semibold text-sm">Total ({currency})</span>
                    <span className="text-xl font-bold font-mono" data-testid="quote-total">{formatCurrency(finalTotal, currency)}</span>
                </div>
            </div>
        </div>
    );
}

// ============ MAIN QUOTE BUILDER ============
export default function QuoteBuilder() {
    const { quoteId } = useParams();
    const navigate = useNavigate();
    const { user, can, canEditQuote } = useAuth();
    const isNew = !quoteId || quoteId === 'new';

    const canOverridePrice = can('override_price');
    const canExport = can('export_quotes');
    const canDelete = can('delete_quotes');

    const [quote, setQuote] = useState(null);
    const [lines, setLines] = useState([]);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [addLineType, setAddLineType] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [editingLine, setEditingLine] = useState(null);
    const [editPriceVal, setEditPriceVal] = useState('');
    const [surchargeApplied, setSurchargeApplied] = useState(false);
    const [internalNotes, setInternalNotes] = useState('');
    const notesTimerRef = useRef(null);

    const [header, setHeader] = useState({
        customer_id: '', customer_name: '', customer_country: '', customer_reference: '',
        customer_type: 'standard',
        currency: 'EUR', incoterm: 'FOB', payment_terms: '',
        validity_days: 30, remarks: '', min_invoice_note: 'Minimum order value: EUR 5,000. Orders below this threshold incur a EUR 400 shipping surcharge.',
        brand: 'LK', billing_entity: '',
    });
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    useEffect(() => {
        if (isNew) return;
        (async () => {
            try {
                const res = await getQuote(quoteId);
                const q = res.data;
                setQuote(q);
                setLines(q.lines || []);
                setHeader({
                    customer_id: q.customer_id || '',
                    customer_name: q.customer_name || '',
                    customer_country: q.customer_country || '',
                    customer_reference: q.customer_reference || '',
                    customer_type: q.customer_type || 'standard',
                    currency: q.currency || 'EUR',
                    incoterm: q.incoterm || 'FOB',
                    payment_terms: q.payment_terms || '',
                    validity_days: q.validity_days || 30,
                    remarks: q.remarks || '',
                    min_invoice_note: q.min_invoice_note || '',
                    brand: q.brand || 'LK',
                    billing_entity: q.billing_entity || '',
                });
                if (q.customer_id) {
                    setSelectedCustomer({ id: q.customer_id, company_name: q.customer_name, country: q.customer_country });
                }
                setSurchargeApplied(q.surcharge_applied || false);
                setInternalNotes(q.internal_notes || '');
            } catch { toast.error('Failed to load quote'); navigate('/quotes'); }
            finally { setLoading(false); }
        })();
    }, [quoteId, isNew, navigate]);

    const onHeaderChange = e => setHeader(p => ({ ...p, [e.target.name]: e.target.value }));

    const handleCustomerSelect = (customer) => {
        setSelectedCustomer(customer);
        const pc = (customer.contacts || []).find(c => c.is_primary) || (customer.contacts || [])[0];
        const defaultShipping = (customer.shipping_addresses || []).find(a => a.is_default) || (customer.shipping_addresses || [])[0];
        const paymentTerms = customer.credit?.payment_terms || '';
        setHeader(prev => ({
            ...prev,
            customer_id: customer.id,
            customer_name: customer.company_name || '',
            customer_country: customer.country || prev.customer_country,
            currency: customer.default_currency || prev.currency,
            payment_terms: paymentTerms || prev.payment_terms,
            billing_entity: customer.default_billing_entity || prev.billing_entity,
        }));
    };

    const handleCustomerClear = () => {
        setSelectedCustomer(null);
        setHeader(prev => ({ ...prev, customer_id: '' }));
    };

    const handleNotesChange = (val) => {
        setInternalNotes(val);
        if (!isNew) {
            clearTimeout(notesTimerRef.current);
            notesTimerRef.current = setTimeout(() => {
                updateInternalNotes('quote', quoteId, val).catch(() => {});
            }, 1000);
        }
    };

    const handleSaveHeader = async () => {
        setSaving(true);
        try {
            const payload = { ...header, surcharge_applied: surchargeApplied };
            if (isNew) {
                const res = await createQuote(payload);
                toast.success(`Quote ${res.data.quote_number} created`);
                navigate(`/quotes/${res.data.id}`, { replace: true });
            } else {
                await updateQuote(quoteId, payload);
                toast.success('Quote saved');
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
        finally { setSaving(false); }
    };

    const handleAddLine = async (lineData) => {
        try {
            const res = await addQuoteLine(quoteId, lineData);
            setLines(prev => [...prev, res.data]);
            setAddLineType(null);
            toast.success('Line added');
            const qRes = await getQuote(quoteId);
            setQuote(qRes.data);
            setLines(qRes.data.lines || []);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add line'); }
    };

    const handleDeleteLine = async (lineId) => {
        try {
            await deleteQuoteLine(quoteId, lineId);
            setLines(prev => prev.filter(l => l.id !== lineId));
            const qRes = await getQuote(quoteId);
            setQuote(qRes.data);
            setLines(qRes.data.lines || []);
            toast.success('Line removed');
        } catch { toast.error('Failed to remove'); }
    };

    const handleUpdateLine = async (lineId, updates) => {
        try {
            await updateQuoteLine(quoteId, lineId, updates);
            setEditingLine(null);
            const qRes = await getQuote(quoteId);
            setQuote(qRes.data);
            setLines(qRes.data.lines || []);
            toast.success('Line updated');
        } catch { toast.error('Failed to update'); }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            await updateQuoteStatus(quoteId, { status: newStatus });
            const qRes = await getQuote(quoteId);
            setQuote(qRes.data);
            toast.success(`Status: ${newStatus}`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    };

    const handleRevise = async () => {
        try {
            const res = await reviseQuote(quoteId, { changes_summary: 'Manual revision' });
            toast.success(res.data.message);
            const qRes = await getQuote(quoteId);
            setQuote(qRes.data);
        } catch { toast.error('Failed to revise'); }
    };

    const handleDuplicate = async () => {
        try {
            const res = await duplicateQuote(quoteId);
            toast.success(`Duplicated as ${res.data.quote_number}`);
            navigate(`/quotes/${res.data.id}`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to duplicate'); }
    };

    const handleDeleteQuote = async () => {
        try {
            await deleteQuote(quoteId);
            toast.success(`Quote ${quote?.quote_number} deleted`);
            navigate('/quotes');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete quote');
        }
        setShowDeleteConfirm(false);
    };

    const handleToggleSurcharge = async () => {
        const newVal = !surchargeApplied;
        setSurchargeApplied(newVal);
        if (!isNew) {
            try { await updateQuote(quoteId, { surcharge_applied: newVal }); }
            catch { /* silently save */ }
        }
    };

    if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-96" /></div>;

    return (
        <div className="space-y-6" data-testid="quote-builder">
            {/* Top bar */}
            <div className="flex items-center justify-between">
                <div>
                    <Button variant="ghost" size="sm" asChild className="mb-1 -ml-2">
                        <Link to="/quotes"><ArrowLeft size={16} className="mr-1" />Quotes</Link>
                    </Button>
                    <h1 className="text-2xl font-bold tracking-tight" data-testid="quote-title">
                        {isNew ? 'New Quote' : quote?.quote_number || 'Quote'}
                    </h1>
                    {quote && <p className="text-sm text-muted-foreground">V{quote.version} — {quote.status}</p>}
                </div>
                <div className="flex gap-2">
                    {!isNew && (
                        <>
                            <Button variant="outline" size="sm" asChild>
                                <Link to={`/quotes/${quoteId}/preview`}><Eye size={16} className="mr-2" />Preview</Link>
                            </Button>
                            {canExport && (
                                <Button variant="outline" size="sm" onClick={() => { const url = getQuotePdfUrl(quoteId); window.open(url, '_blank'); }} data-testid="export-pdf-btn">
                                    <FilePdf size={16} className="mr-2" />Export PDF
                                </Button>
                            )}
                            {canEditQuote(quote) && (
                                <>
                                    <Select value={quote?.status || 'draft'} onValueChange={handleStatusChange}>
                                        <SelectTrigger className="w-[160px] h-9" data-testid="status-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {statusOptions.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    <Button variant="outline" size="sm" onClick={handleRevise} data-testid="revise-btn">
                                        <ArrowsClockwise size={16} className="mr-1" /> Revise
                                    </Button>
                                </>
                            )}
                            {can('create_quotes') && (
                                <Button variant="outline" size="sm" onClick={handleDuplicate} data-testid="duplicate-btn">
                                    <Copy size={16} className="mr-1" /> Duplicate
                                </Button>
                            )}
                            {canDelete && quote?.status === 'draft' && (
                                <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => setShowDeleteConfirm(true)} data-testid="delete-quote-btn">
                                    <Trash size={16} className="mr-1" /> Delete
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Quote Header Form */}
            <div className="bg-card border border-border rounded-lg">
                <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
                    <h3 className="font-semibold text-sm">Quote Header</h3>
                    {(isNew || canEditQuote(quote)) && (
                        <Button size="sm" onClick={handleSaveHeader} disabled={saving} data-testid="save-header-btn">
                            <FloppyDisk size={16} className="mr-2" />{saving ? 'Saving...' : isNew ? 'Create Quote' : 'Save Header'}
                        </Button>
                    )}
                </div>
                <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
                    <div className="md:col-span-2">
                        <Label className="text-xs text-muted-foreground mb-1 block">Customer</Label>
                        <CustomerPicker
                            value={selectedCustomer}
                            onSelect={handleCustomerSelect}
                            onClear={handleCustomerClear}
                            placeholder="Search by company, contact name or email..."
                            disabled={!isNew && !canEditQuote(quote)}
                        />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Reference</Label>
                        <Input name="customer_reference" value={header.customer_reference} onChange={onHeaderChange} />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Customer Name {selectedCustomer && <span className="text-[10px] text-muted-foreground/70">(overridable)</span>}</Label>
                        <Input name="customer_name" value={header.customer_name} onChange={onHeaderChange} data-testid="field-customer_name" />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Country {selectedCustomer && <span className="text-[10px] text-muted-foreground/70">(overridable)</span>}</Label>
                        <Input name="customer_country" value={header.customer_country} onChange={onHeaderChange} data-testid="field-customer_country" />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Customer Type</Label>
                        <Select value={header.customer_type} onValueChange={v => setHeader(p => ({ ...p, customer_type: v }))} data-testid="field-customer_type">
                            <SelectTrigger data-testid="field-customer_type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="standard">Standard</SelectItem>
                                <SelectItem value="dealer">Dealer</SelectItem>
                                <SelectItem value="distributor">Distributor</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Currency {selectedCustomer && <span className="text-[10px] text-muted-foreground/70">(overridable)</span>}</Label>
                        <Select value={header.currency} onValueChange={v => setHeader(p => ({ ...p, currency: v }))}>
                            <SelectTrigger data-testid="field-currency"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="EUR">EUR</SelectItem>
                                <SelectItem value="USD">USD</SelectItem>
                                <SelectItem value="GBP">GBP</SelectItem>
                                <SelectItem value="INR">INR</SelectItem>
                                <SelectItem value="AED">AED</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Incoterm</Label>
                        <Select value={header.incoterm} onValueChange={v => setHeader(p => ({ ...p, incoterm: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {['FOB', 'CIF', 'CFR', 'EXW', 'DDP', 'DAP', 'FCA'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Validity (days)</Label>
                        <Input name="validity_days" type="number" value={header.validity_days} onChange={onHeaderChange} className="font-mono" />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Payment Terms {selectedCustomer && <span className="text-[10px] text-muted-foreground/70">(overridable)</span>}</Label>
                        <Input name="payment_terms" value={header.payment_terms} onChange={onHeaderChange} placeholder="e.g. 30% advance, 70% LC" />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Billing Entity {selectedCustomer && <span className="text-[10px] text-muted-foreground/70">(overridable)</span>}</Label>
                        <Input name="billing_entity" value={header.billing_entity} onChange={onHeaderChange} placeholder="e.g. Crown Decor" data-testid="field-billing_entity" />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Brand Prefix</Label>
                        <Input name="brand" value={header.brand} onChange={onHeaderChange} className="font-mono" />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground mb-1 block">Min Invoice Note</Label>
                        <Input name="min_invoice_note" value={header.min_invoice_note} onChange={onHeaderChange} />
                    </div>
                    <div className="md:col-span-3">
                        <Label className="text-xs text-muted-foreground mb-1 block">Remarks</Label>
                        <Input name="remarks" value={header.remarks} onChange={onHeaderChange} />
                    </div>
                </div>
            </div>

            {/* Line Items */}
            {!isNew && (
                <div className="bg-card border border-border rounded-lg">
                    <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-sm">Line Items</h3>
                            <Badge variant="outline" className="font-mono">{lines.length}</Badge>
                        </div>
                        {canEditQuote(quote) && (
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setAddLineType('catalog')} data-testid="add-catalog-btn">
                                    <Package size={15} className="mr-1.5" /> Catalog
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setAddLineType('custom')} data-testid="add-custom-btn">
                                    <Plus size={15} className="mr-1.5" /> Custom
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setAddLineType('manual')} data-testid="add-manual-btn">
                                    <Plus size={15} className="mr-1.5" /> Manual
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-8">#</TableHead>
                                    <TableHead className="w-12"></TableHead>
                                    <TableHead>Product</TableHead>
                                    <TableHead className="text-right">Rec. MOQ</TableHead>
                                    <TableHead className="text-right">Price</TableHead>
                                    <TableHead className="text-right w-[100px]">Qty</TableHead>
                                    <TableHead className="text-right">Total</TableHead>
                                    <TableHead className="w-[50px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {lines.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                                            No line items yet. Add products from Catalog, Custom, or Manual.
                                        </TableCell>
                                    </TableRow>
                                ) : lines.map((line, idx) => {
                                    const belowMoq = line.line_type === 'catalog' && line.moq && line.quantity > 0 && line.quantity < line.moq;
                                    const priceOverridden = line.quoted_price !== line.default_price && line.default_price > 0;

                                    return (
                                        <TableRow key={line.id} data-testid={`line-row-${line.id}`} className={belowMoq ? 'bg-amber-50/30 dark:bg-amber-950/10' : ''}>
                                            <TableCell className="text-muted-foreground text-xs font-mono">{idx + 1}</TableCell>
                                            <TableCell>
                                                {line.line_type === 'manual' ? (
                                                    <div className="w-12 h-12 rounded-md bg-muted/60 border border-border/40 flex items-center justify-center">
                                                        <span className="text-sm font-mono text-muted-foreground/70">$</span>
                                                    </div>
                                                ) : (
                                                    <ProductImage src={line.image_url} alt={line.product_name} size="sm" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="space-y-0.5">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <span className="font-mono text-xs font-semibold">{line.display_sku || line.display_code}</span>
                                                        <Badge variant="outline" className="text-[9px] h-4 capitalize">{line.line_type}</Badge>
                                                    </div>
                                                    <div className="text-sm leading-tight">{line.product_name || line.description}</div>
                                                    {line.line_type !== 'manual' && (
                                                        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground flex-wrap">
                                                            {line.dimensions && <span>{dimWithUnit(line.dimensions)}</span>}
                                                            {line.thickness && <span>| {line.thickness}</span>}
                                                            {line.decor_no && <span>| D{line.decor_no}</span>}
                                                            {line.finish && <span>| {line.finish}</span>}
                                                        </div>
                                                    )}
                                                    {line.notes && <div className="text-[11px] text-muted-foreground italic">{line.notes}</div>}
                                                    {priceOverridden && line.override_reason && (
                                                        <div className="text-[10px] text-amber-600 dark:text-amber-400">Override: {line.override_reason}</div>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="font-mono text-xs">{line.moq?.toLocaleString() || '—'}</div>
                                                {belowMoq && (
                                                    <div className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center justify-end gap-0.5" data-testid={`moq-warn-${line.id}`}>
                                                        <WarningCircle size={10} /> Below
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {editingLine === line.id ? (
                                                    <div className="flex flex-col gap-1 items-end">
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            className="w-24 h-7 text-right font-mono text-xs"
                                                            value={editPriceVal}
                                                            onChange={e => setEditPriceVal(e.target.value)}
                                                            data-testid="edit-price"
                                                            onBlur={() => {
                                                                const np = parseFloat(editPriceVal) || 0;
                                                                if (np !== line.quoted_price) {
                                                                    handleUpdateLine(line.id, { quoted_price: np, quantity: line.quantity });
                                                                } else setEditingLine(null);
                                                            }}
                                                            onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                                                            autoFocus
                                                        />
                                                        {priceOverridden && (
                                                            <span className="text-[10px] text-muted-foreground line-through">{formatCurrency(line.default_price, line.currency)}</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <button
                                                        className={`font-mono text-xs text-right ${canOverridePrice ? 'hover:text-primary cursor-pointer' : 'cursor-default'} transition-colors`}
                                                        onClick={() => { if (canOverridePrice) { setEditingLine(line.id); setEditPriceVal(String(line.quoted_price)); } }}
                                                        data-testid={`price-${line.id}`}
                                                        disabled={!canOverridePrice}
                                                    >
                                                        <div>{formatCurrency(line.quoted_price, line.currency)}</div>
                                                        {priceOverridden && (
                                                            <div className="text-[10px] text-muted-foreground line-through">{formatCurrency(line.default_price, line.currency)}</div>
                                                        )}
                                                    </button>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Input
                                                    type="number"
                                                    className="w-20 h-7 text-right font-mono text-xs ml-auto"
                                                    value={line.quantity || ''}
                                                    onChange={e => {
                                                        const q = parseInt(e.target.value) || 0;
                                                        setLines(prev => prev.map(l => l.id === line.id ? { ...l, quantity: q, line_total: Math.round(l.quoted_price * q * 100) / 100 } : l));
                                                    }}
                                                    onBlur={e => {
                                                        const q = parseInt(e.target.value) || 0;
                                                        if (q !== (line._origQty ?? line.quantity)) handleUpdateLine(line.id, { quantity: q, quoted_price: line.quoted_price });
                                                    }}
                                                    onFocus={e => { const l = lines.find(x => x.id === line.id); if (l) l._origQty = l.quantity; }}
                                                    data-testid={`qty-${line.id}`}
                                                />
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-xs font-semibold">
                                                {formatCurrency(line.line_total || 0, line.currency)}
                                            </TableCell>
                                            <TableCell>
                                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteLine(line.id)} data-testid={`delete-line-${line.id}`}>
                                                    <Trash size={14} />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Commercial Summary */}
                    {lines.length > 0 && (
                        <CommercialSummary
                            lines={lines}
                            currency={header.currency}
                            surchargeApplied={surchargeApplied}
                            onToggleSurcharge={handleToggleSurcharge}
                        />
                    )}
                </div>
            )}

            {/* Internal Notes */}
            {!isNew && (
                <div className="bg-card border border-border rounded-lg">
                    <div className="px-5 py-3 border-b border-border">
                        <h3 className="font-semibold text-sm">Internal Notes</h3>
                    </div>
                    <div className="p-4">
                        <textarea
                            value={internalNotes}
                            onChange={e => handleNotesChange(e.target.value)}
                            placeholder="Add internal notes about this quote (auto-saves)..."
                            className="w-full min-h-[60px] text-sm bg-muted/30 border border-border rounded-md p-3 resize-y focus:outline-none focus:ring-1 focus:ring-ring"
                            data-testid="internal-notes"
                        />
                    </div>
                </div>
            )}

            {/* Add Line Dialog */}
            <Dialog open={addLineType !== null} onOpenChange={(open) => !open && setAddLineType(null)}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto z-[60]">
                    <DialogHeader>
                        <DialogTitle>
                            {addLineType === 'catalog' ? 'Add Catalog Product' : addLineType === 'custom' ? 'Add Custom Product' : 'Add Manual Line'}
                        </DialogTitle>
                        <DialogDescription>
                            {addLineType === 'catalog' ? 'Search and configure a catalog product to add to this quote.' : addLineType === 'custom' ? 'Select a custom product to add to this quote.' : 'Enter details for a manual line item.'}
                        </DialogDescription>
                    </DialogHeader>
                    {addLineType === 'catalog' && <CatalogConfigurator currency={header.currency} onAdd={handleAddLine} onClose={() => setAddLineType(null)} />}
                    {addLineType === 'custom' && <CustomProductSelector currency={header.currency} onAdd={handleAddLine} />}
                    {addLineType === 'manual' && <ManualLineForm currency={header.currency} onAdd={handleAddLine} />}
                </DialogContent>
            </Dialog>

            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent data-testid="delete-quote-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Quote</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete <span className="font-semibold">{quote?.quote_number}</span>? This will permanently remove the quote and all its line items. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteQuote} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-confirm-btn">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
